/**
 * Resource Gating System
 *
 * This module handles the gating of marketing resources (PDFs, videos, etc.) behind
 * HubSpot forms. Users must submit a form to gain access to gated content.
 *
 * ## How it works:
 *
 * 1. **Initial State**: When a user visits a gated resource page:
 *    - The actual resource content (.resource-wrapper) is hidden via CSS
 *    - A HubSpot form (.form-wrapper) is shown instead
 *
 * 2. **Access Check**: Before loading the form, we check if the user already
 *    has access via a cookie (gated_resource_access=true)
 *
 * 3. **Form Submission**: When the user submits the form:
 *    - A cookie is set granting access to all gated resources
 *    - The form is removed from the DOM
 *    - The actual resource content is revealed
 *    - For PDFs: The object element's data attribute is populated with the file URL
 *
 * ## Template Integration:
 *
 * In show.html.slim, gated resources use this structure:
 *
 * ```slim
 * - if resource.gated
 *   div class="form-wrapper hidden" // HubSpot form container
 *
 * div class="resource-wrapper #{resource.gated && 'hidden'}" // Resource content
 *   object data=(resource.gated ? "" : resource.file_download_url) // PDF with conditional URL
 *   // OR
 *   div class="shadow-1 rounded-3xl overflow-hidden" // Video embed
 *     == render "shared/wistia_embed", wistia_id: resource.resource_asset.wistia_id, gated: resource.gated
 * ```
 *
 * ## Cookie Behavior:
 *
 * - Cookie name: "gated_resource_access"
 * - Scope: Site-wide (path=/)
 * - Duration: Session-based (expires when browser closes)
 * - Effect: Grants access to ALL gated resources across the site
 */

export interface ResourceData {
  title: string
  secret: string
  url?: string // Only required for PDFs
}

export function checkGatedAccess({ url, secret }: ResourceData): boolean {
  const hasAccess =
    document.cookie.includes("gated_resource_access=true") ||
    window.location.href.includes(secret)
  if (!hasAccess) {
    const formContainer = document.querySelector(".form-wrapper")
    formContainer?.classList.remove("hidden")
  } else {
    ungateResource(url)
  }
  return hasAccess
}

export function ungateResource(url?: string) {
  setGatedResourceAccess()
  if (url) {
    loadGatedFile(url)
  }
  const formContainer = document.querySelector(".form-wrapper")
  formContainer?.remove()

  const resourceContainer = document.querySelector(".resource-wrapper")
  resourceContainer?.classList.remove("hidden")
}

function setGatedResourceAccess() {
  const expiry = new Date()
  expiry.setMonth(expiry.getMonth() + 6)
  document.cookie = `gated_resource_access=true;path=/;expires=${expiry.toUTCString()}`
}

function loadGatedFile(url: string) {
  const resourceObject = document.querySelector("object")
  if (resourceObject) {
    resourceObject.setAttribute("data", url)
  }
}
